import {
  EmptyState,
  FlexColumn,
  FlexRow,
  Loading,
  MaterialIcon,
  useLoading,
  useMoment,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { HeadingSmall } from 'baseui/typography';
import { useEffect } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { useStyles } from '../../hooks/useStyles';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import { getLinkDetail } from '../../services/linkService';
import { apiParamsState } from '../../storage/app';
import { linkDetailState, linkState, selectedLinkIdState } from '../../storage/links';
import { EvaluationCard } from '../evaluations/EvaluationCard';
import { FloatingCardContent } from '../layout/cards/FloatingCardContent';
import { StyledTooltip } from '../ui/StyledTooltip';
import LinkAssetCard from './LinkAssetCard';

export function LinkDetail() {
  const apiParams = useRecoilValue(apiParamsState);
  const { formatDateFromTs } = useMoment();
  const { theme, css } = useStyles();
  const selectedLinkId = useRecoilValue(selectedLinkIdState);
  const resetSelectedLinkId = useResetRecoilState(selectedLinkIdState);
  const link = useRecoilValue(linkState(selectedLinkId ?? ''));
  const [linkDetail, setLinkDetail] = useRecoilState(linkDetailState(selectedLinkId ?? ''));
  const { loading, startLoading, stopLoading } = useLoading(true);

  useUpdateEffect(() => {
    resetSelectedLinkId();
  }, [apiParams?.clientId, apiParams?.subClientId]);

  useEffect(() => {
    if (!apiParams || !selectedLinkId) return;
    startLoading();
    const loadLinkDetails = async () => {
      const { linkDetail, error } = await getLinkDetail(apiParams.clientId, apiParams.subClientId, selectedLinkId);
      if (!error && linkDetail) {
        setLinkDetail(linkDetail);
      }
      stopLoading();
    };

    loadLinkDetails();
  }, [selectedLinkId]);

  if (!link) return null;

  return (
    <FloatingCardContent
      flexible
      gap={theme.sizing.scale600}
    >
      {!linkDetail && !loading && <EmptyState title="No se pudo cargar el detalle" />}
      {!linkDetail && loading && <Loading />}
      {linkDetail && (
        <>
          <FlexRow classNames={css({ alignItems: 'center', justifyContent: 'space-between' })}>
            <HeadingSmall margin={0}>Vinculación - {formatDateFromTs(link.created)}</HeadingSmall>
            <StyledTooltip
              content="Cerrar"
              showArrow={false}
            >
              <Button
                shape="circle"
                kind="tertiary"
                size="compact"
                onClick={resetSelectedLinkId}
              >
                <MaterialIcon
                  name="close"
                  size="mini"
                />
              </Button>
            </StyledTooltip>
          </FlexRow>
          <div style={{ flex: 1 }}>
            <AutoSizer>
              {({ width, height }) => (
                <FlexColumn
                  classNames={css({
                    width: `${width}px`,
                    height: `${height}px`,
                    overflowY: 'auto',
                    position: 'relative',
                  })}
                >
                  <LinkAssetCard
                    assetDetail={linkDetail.assetPrimary}
                    isPrimary
                  />
                  <LinkAssetCard assetDetail={linkDetail.assetSecondary} />
                  {!linkDetail.evaluations.length && <EmptyState title="No existen evaluaciones" />}
                  {linkDetail.evaluations.map((evaluation, evaluationIndex) => (
                    <EvaluationCard
                      evaluation={evaluation}
                      key={evaluationIndex}
                    />
                  ))}
                </FlexColumn>
              )}
            </AutoSizer>
          </div>
        </>
      )}
    </FloatingCardContent>
  );
}
